import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Microphone, Play, Pause, Download, SpeakerHigh, User, UserCircle, CheckCircle, TextAa, ListBullets } from '@phosphor-icons/react';
import { useLanguage } from '@/contexts/LanguageContext';
import JSZip from 'jszip';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const voiceOptionsEN = [
  { id: 'alloy', name: 'Alloy', description: 'Neutral, balanced', gender: 'neutral', color: '#6B7280' },
  { id: 'ash', name: 'Ash', description: 'Clear, articulate', gender: 'male', color: '#3B82F6' },
  { id: 'coral', name: 'Coral', description: 'Warm, friendly', gender: 'female', color: '#EC4899' },
  { id: 'echo', name: 'Echo', description: 'Smooth, calm', gender: 'male', color: '#3B82F6' },
  { id: 'fable', name: 'Fable', description: 'Expressive, storytelling', gender: 'neutral', color: '#6B7280' },
  { id: 'nova', name: 'Nova', description: 'Energetic, upbeat', gender: 'female', color: '#EC4899' },
  { id: 'onyx', name: 'Onyx', description: 'Deep, authoritative', gender: 'male', color: '#3B82F6' },
  { id: 'sage', name: 'Sage', description: 'Wise, measured', gender: 'neutral', color: '#6B7280' },
  { id: 'shimmer', name: 'Shimmer', description: 'Bright, cheerful', gender: 'female', color: '#EC4899' },
];

const voiceOptionsES = [
  { id: 'alloy', name: 'Alloy', description: 'Neutral, equilibrada', gender: 'neutral', color: '#6B7280' },
  { id: 'ash', name: 'Ash', description: 'Clara, articulada', gender: 'male', color: '#3B82F6' },
  { id: 'coral', name: 'Coral', description: 'Cálida, amigable', gender: 'female', color: '#EC4899' },
  { id: 'echo', name: 'Echo', description: 'Suave, calmada', gender: 'male', color: '#3B82F6' },
  { id: 'fable', name: 'Fable', description: 'Expresiva, narrativa', gender: 'neutral', color: '#6B7280' },
  { id: 'nova', name: 'Nova', description: 'Energética, optimista', gender: 'female', color: '#EC4899' },
  { id: 'onyx', name: 'Onyx', description: 'Profunda, autoritaria', gender: 'male', color: '#3B82F6' },
  { id: 'sage', name: 'Sage', description: 'Sabia, mesurada', gender: 'neutral', color: '#6B7280' },
  { id: 'shimmer', name: 'Shimmer', description: 'Brillante, alegre', gender: 'female', color: '#EC4899' },
];

export const VoiceoverModal = ({ open, onClose, projectId, scenes, onGenerated }) => {
  const { language } = useLanguage();
  const voiceOptions = language === 'es' ? voiceOptionsES : voiceOptionsEN;
  
  const [selectedVoice, setSelectedVoice] = useState('nova');
  const [speed, setSpeed] = useState([1.0]);
  const [model, setModel] = useState('tts-1');
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [previewAudio, setPreviewAudio] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [textMode, setTextMode] = useState('scenes'); // 'scenes' or 'custom'
  const [customText, setCustomText] = useState('');
  const [customAudioUrl, setCustomAudioUrl] = useState(null);
  const audioRef = useRef(null);

  const scenesWithText = scenes.filter(s => s.script_excerpt || s.summary);
  const scenesWithVoiceover = scenes.filter(s => s.voiceover_url);

  const handleGenerateAll = async () => {
    if (scenesWithText.length === 0) {
      toast.error(language === 'es' ? 'No hay escenas con texto para generar voces' : 'No scenes with text to generate voiceovers');
      return;
    }

    const confirmMsg = language === 'es' 
      ? `¿Generar voces para ${scenesWithText.length} escenas usando la voz "${voiceOptions.find(v => v.id === selectedVoice)?.name}"?\n\nEsto puede tomar unos minutos.`
      : `Generate voiceovers for ${scenesWithText.length} scenes using "${voiceOptions.find(v => v.id === selectedVoice)?.name}" voice?\n\nThis may take a few minutes.`;
    
    const confirmed = window.confirm(confirmMsg);
    if (!confirmed) return;

    setGenerating(true);
    setProgress({ current: 0, total: scenesWithText.length });

    try {
      // Generate one by one for progress tracking
      for (let i = 0; i < scenesWithText.length; i++) {
        const scene = scenesWithText[i];
        setProgress({ current: i + 1, total: scenesWithText.length });
        toast.info(`Generating voiceover for scene ${scene.scene_number}... (${i + 1}/${scenesWithText.length})`, { id: 'vo-progress' });

        const text = scene.script_excerpt || scene.summary;
        
        try {
          await axios.post(`${API}/scenes/${scene.id}/generate-voiceover`, {
            text: text,
            voice: selectedVoice,
            speed: speed[0],
            model: model
          });
        } catch (err) {
          console.error(`Failed to generate voiceover for scene ${scene.scene_number}:`, err);
        }
      }

      toast.success(`Generated voiceovers for ${scenesWithText.length} scenes!`, { id: 'vo-progress' });
      
      if (onGenerated) {
        onGenerated();
      }

    } catch (err) {
      toast.error('Voiceover generation failed');
      console.error(err);
    } finally {
      setGenerating(false);
      setProgress({ current: 0, total: 0 });
    }
  };

  const handleGenerateCustom = async () => {
    if (!customText.trim()) {
      toast.error('Please enter some text to generate voiceover');
      return;
    }

    if (customText.length > 20000) {
      toast.error('Text is too long. Maximum 20,000 characters allowed.');
      return;
    }

    setGenerating(true);
    
    const chunks = Math.ceil(customText.length / 4000);
    if (chunks > 1) {
      toast.info(`Generating voiceover (${customText.length.toLocaleString()} chars in ${chunks} parts)... This may take a minute.`);
    } else {
      toast.info('Generating custom voiceover...');
    }

    try {
      const res = await axios.post(`${API}/voiceover/generate-custom`, {
        text: customText,
        voice: selectedVoice,
        speed: speed[0],
        model: model
      });

      if (res.data.audio_url) {
        setCustomAudioUrl(res.data.audio_url);
        toast.success(`Custom voiceover generated! (${res.data.text_length.toLocaleString()} characters)`);
      }
    } catch (err) {
      toast.error('Failed to generate custom voiceover');
      console.error(err);
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadAll = async () => {
    if (scenesWithVoiceover.length === 0) {
      toast.error('No voiceovers to download. Generate them first.');
      return;
    }

    toast.info('Preparing voiceover ZIP...');

    try {
      const zip = new JSZip();
      const audioFolder = zip.folder('voiceovers');

      for (const scene of scenesWithVoiceover) {
        if (scene.voiceover_url && scene.voiceover_url.startsWith('data:')) {
          const [header, base64Data] = scene.voiceover_url.split(',');
          const filename = `scene_${String(scene.scene_number).padStart(3, '0')}_voiceover.mp3`;
          audioFolder.file(filename, base64Data, { base64: true });
        }
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      
      const url = URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'voiceovers.zip';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(`Downloaded ${scenesWithVoiceover.length} voiceover files!`);
    } catch (err) {
      toast.error('Failed to create ZIP');
      console.error(err);
    }
  };

  const handlePreview = (audioUrl) => {
    if (audioRef.current) {
      if (previewAudio === audioUrl && isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        setPreviewAudio(audioUrl);
        audioRef.current.src = audioUrl;
        audioRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.onended = () => setIsPlaying(false);
    }
  }, []);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#171717] border-[#333333] text-white max-w-2xl max-h-[85vh]">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold flex items-center gap-2">
            <Microphone size={24} weight="fill" className="text-[#FF5D01]" />
            {language === 'es' ? 'Generador de Voz con IA' : 'AI Voiceover Generator'}
          </DialogTitle>
        </DialogHeader>

        <audio ref={audioRef} className="hidden" />

        <div className="py-4 space-y-5">
          {/* Voice Selection */}
          <div>
            <Label className="text-[#A1A1AA] text-xs uppercase tracking-wider mb-3 block">
              {language === 'es' ? 'Seleccionar Voz' : 'Select Voice'}
            </Label>
            <div className="grid grid-cols-3 gap-2">
              {voiceOptions.map((voice) => {
                const isSelected = selectedVoice === voice.id;
                const GenderIcon = voice.gender === 'female' ? UserCircle : voice.gender === 'male' ? User : SpeakerHigh;
                
                return (
                  <button
                    key={voice.id}
                    onClick={() => setSelectedVoice(voice.id)}
                    className={`p-3 rounded-lg border transition-all text-left ${
                      isSelected 
                        ? 'border-[#FF5D01] bg-[#FF5D01]/10' 
                        : 'border-[#333333] bg-[#0a0a0a] hover:border-[#444]'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <GenderIcon size={16} style={{ color: voice.color }} />
                        <span className="text-sm font-medium">{voice.name}</span>
                      </div>
                      <span 
                        className="text-[10px] px-1.5 py-0.5 rounded uppercase font-medium"
                        style={{ 
                          backgroundColor: voice.gender === 'female' ? '#EC489920' : voice.gender === 'male' ? '#3B82F620' : '#6B728020',
                          color: voice.color
                        }}
                      >
                        {voice.gender === 'female' ? (language === 'es' ? 'Femenina' : 'Female') : voice.gender === 'male' ? (language === 'es' ? 'Masculina' : 'Male') : 'Neutral'}
                      </span>
                    </div>
                    <p className="text-xs text-[#A1A1AA]">{voice.description}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Speed & Model */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-[#A1A1AA] text-xs uppercase tracking-wider mb-2 block">
                {language === 'es' ? 'Velocidad' : 'Speed'}: {speed[0].toFixed(1)}x
              </Label>
              <Slider
                value={speed}
                onValueChange={setSpeed}
                min={0.5}
                max={2.0}
                step={0.1}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-[#666] mt-1">
                <span>{language === 'es' ? 'Más lento' : 'Slower'}</span>
                <span>{language === 'es' ? 'Más rápido' : 'Faster'}</span>
              </div>
            </div>
            <div>
              <Label className="text-[#A1A1AA] text-xs uppercase tracking-wider mb-2 block">
                {language === 'es' ? 'Calidad' : 'Quality'}
              </Label>
              <Select value={model} onValueChange={setModel}>
                <SelectTrigger className="bg-[#0a0a0a] border-[#333333] text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#171717] border-[#333333] text-white">
                  <SelectItem value="tts-1">{language === 'es' ? 'Estándar (más rápido)' : 'Standard (faster)'}</SelectItem>
                  <SelectItem value="tts-1-hd">{language === 'es' ? 'HD (más lento)' : 'HD Quality (slower)'}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Text Source - Tabs for Scenes vs Custom */}
          <div>
            <Tabs value={textMode} onValueChange={setTextMode} className="w-full">
              <TabsList className="grid w-full grid-cols-2 bg-[#0a0a0a] border border-[#333333]">
                <TabsTrigger 
                  value="scenes" 
                  className="data-[state=active]:bg-[#FF5D01] data-[state=active]:text-white flex items-center gap-2"
                >
                  <ListBullets size={16} />
                  {language === 'es' ? 'Guiones de Escenas' : 'Scene Scripts'}
                </TabsTrigger>
                <TabsTrigger 
                  value="custom" 
                  className="data-[state=active]:bg-[#FF5D01] data-[state=active]:text-white flex items-center gap-2"
                >
                  <TextAa size={16} />
                  {language === 'es' ? 'Texto Personalizado' : 'Custom Text'}
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="scenes" className="mt-3">
                <Label className="text-[#A1A1AA] text-xs uppercase tracking-wider mb-2 block">
                  {language === 'es' ? `Estado de Escenas (${scenesWithVoiceover.length}/${scenes.length} tienen voz)` : `Scene Status (${scenesWithVoiceover.length}/${scenes.length} have voiceovers)`}
                </Label>
                <ScrollArea className="h-[180px] border border-[#333333] rounded-lg">
                  <div className="p-2 space-y-1">
                    {scenes.map((scene) => {
                      const hasVoiceover = !!scene.voiceover_url;
                      const hasText = !!(scene.script_excerpt || scene.summary);
                      
                      return (
                        <div
                          key={scene.id}
                          className={`flex items-center gap-3 p-2 rounded-lg ${
                            hasVoiceover ? 'bg-[#10B981]/10' : 'bg-[#0a0a0a]'
                          }`}
                        >
                          <div className="w-8 h-8 bg-[#262626] rounded flex items-center justify-center text-xs font-medium text-[#FF5D01]">
                            #{scene.scene_number}
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-[#A1A1AA] truncate">
                              {scene.script_excerpt?.substring(0, 60) || scene.summary?.substring(0, 60) || (language === 'es' ? 'Sin texto' : 'No text')}...
                            </p>
                          </div>

                          {hasVoiceover ? (
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handlePreview(scene.voiceover_url)}
                                className="h-7 w-7 p-0 text-[#10B981] hover:text-white hover:bg-[#262626]"
                              >
                                {previewAudio === scene.voiceover_url && isPlaying ? (
                                  <Pause size={14} weight="fill" />
                                ) : (
                                  <Play size={14} weight="fill" />
                                )}
                              </Button>
                              <CheckCircle size={16} weight="fill" className="text-[#10B981]" />
                            </div>
                          ) : hasText ? (
                            <span className="text-xs text-[#666]">Pending</span>
                          ) : (
                            <span className="text-xs text-[#EF4444]">No text</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              </TabsContent>
              
              <TabsContent value="custom" className="mt-3">
                <Label className="text-[#A1A1AA] text-xs uppercase tracking-wider mb-2 block">
                  Enter Custom Text (Intro, Outro, or any text)
                </Label>
                <Textarea
                  value={customText}
                  onChange={(e) => setCustomText(e.target.value)}
                  placeholder={language === 'es' ? 'Pega tu intro, outro o cualquier texto personalizado aquí para generar voz en off...' : 'Paste your intro, outro, or any custom text here to generate voiceover...'}
                  className="bg-[#0a0a0a] border-[#333333] text-white min-h-[140px] resize-none"
                />
                <div className="flex items-center justify-between mt-2">
                  <span className="text-xs text-[#666]">
                    {customText.length.toLocaleString()} / 20,000 {language === 'es' ? 'caracteres' : 'characters'}
                    {customText.length > 4000 && (
                      <span className="ml-2 text-[#F59E0B]">
                        ({language === 'es' ? `Se procesará en ${Math.ceil(customText.length / 4000)} partes` : `Will be processed in ${Math.ceil(customText.length / 4000)} parts`})
                      </span>
                    )}
                  </span>
                  {customAudioUrl && (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handlePreview(customAudioUrl)}
                        className="h-7 px-2 text-[#10B981] hover:text-white hover:bg-[#262626] gap-1"
                      >
                        {previewAudio === customAudioUrl && isPlaying ? (
                          <Pause size={14} weight="fill" />
                        ) : (
                          <Play size={14} weight="fill" />
                        )}
                        {language === 'es' ? 'Escuchar' : 'Preview'}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const link = document.createElement('a');
                          link.href = customAudioUrl;
                          link.download = 'custom_voiceover.mp3';
                          link.click();
                        }}
                        className="h-7 px-2 text-[#A1A1AA] hover:text-white hover:bg-[#262626] gap-1"
                      >
                        <Download size={14} />
                        {language === 'es' ? 'Descargar' : 'Download'}
                      </Button>
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </div>

          {/* Progress */}
          {generating && (
            <div className="p-3 bg-[#0a0a0a] rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm">{language === 'es' ? 'Generando voces en off...' : 'Generating voiceovers...'}</span>
                <span className="text-sm text-[#FF5D01]">{progress.current}/{progress.total}</span>
              </div>
              <div className="w-full h-2 bg-[#333333] rounded-full overflow-hidden">
                <div 
                  className="h-full bg-[#FF5D01] rounded-full transition-all duration-300"
                  style={{ width: `${(progress.current / progress.total) * 100}%` }}
                />
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex justify-between">
          {textMode === 'scenes' ? (
            <>
              <Button
                variant="outline"
                onClick={handleDownloadAll}
                disabled={generating || scenesWithVoiceover.length === 0}
                className="border-[#333333] text-white hover:bg-[#262626] gap-2"
              >
                <Download size={16} />
                {language === 'es' ? `Descargar Todo (${scenesWithVoiceover.length})` : `Download All (${scenesWithVoiceover.length})`}
              </Button>
              <div className="flex gap-2">
                <Button variant="ghost" onClick={onClose} disabled={generating} className="text-[#A1A1AA] hover:text-white">
                  {language === 'es' ? 'Cerrar' : 'Close'}
                </Button>
                <Button
                  onClick={handleGenerateAll}
                  disabled={generating || scenesWithText.length === 0}
                  className="bg-[#FF5D01] hover:bg-[#e65400] text-white gap-2"
                  data-testid="generate-voiceovers-btn"
                >
                  {generating ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Microphone size={18} weight="fill" />
                  )}
                  {generating ? `${language === 'es' ? 'Generando' : 'Generating'} ${progress.current}/${progress.total}...` : `${language === 'es' ? 'Generar Todo' : 'Generate All'} (${scenesWithText.length})`}
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="text-xs text-[#A1A1AA]">
                {language === 'es' ? 'Usa texto personalizado para intros, outros o cualquier narración que no esté en las escenas' : 'Use custom text for intros, outros, or any narration not in scenes'}
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" onClick={onClose} disabled={generating} className="text-[#A1A1AA] hover:text-white">
                  {language === 'es' ? 'Cerrar' : 'Close'}
                </Button>
                <Button
                  onClick={handleGenerateCustom}
                  disabled={generating || !customText.trim()}
                  className="bg-[#FF5D01] hover:bg-[#e65400] text-white gap-2"
                  data-testid="generate-custom-voiceover-btn"
                >
                  {generating ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Microphone size={18} weight="fill" />
                  )}
                  {generating ? (language === 'es' ? 'Generando...' : 'Generating...') : (language === 'es' ? 'Generar Voz Personalizada' : 'Generate Custom Voiceover')}
                </Button>
              </div>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
